/**
 * EmailListVerify, which checks email addresses before marketing is sent to
 * them (promised to AWS with SES production access).
 *
 * The key is write-only, as the SES secret is: the box starts empty, the
 * server says only whether one is saved and its last four characters, and
 * it is written only when something is typed. "Test" asks EmailListVerify
 * for the credit balance, with the typed key if there is one.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Group, NumberInput, PasswordInput, SegmentedControl, Stack, Switch, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useElvSettings, useSaveElvSettings, useTestElv } from '../../api/queries';
import { formatNumber } from '../../theme';
import { useVocab } from '../../vocab';

export function EmailVerificationCard() {
  const v = useVocab();
  const settings = useElvSettings(true);
  const save = useSaveElvSettings();
  const test = useTestElv();
  const [key, setKey] = useState('');
  const [form, setForm] = useState({
    enabled: false,
    send_ok_for_all: false,
    inconclusive_policy: 'skip',
    cache_days: 90,
    verify_on_arrival: false,
  });
  useEffect(() => {
    if (!settings.data) return;
    const d = settings.data;
    setForm({
      enabled: d.enabled,
      send_ok_for_all: d.send_ok_for_all,
      inconclusive_policy: d.inconclusive_policy,
      cache_days: d.cache_days,
      verify_on_arrival: d.verify_on_arrival,
    });
  }, [settings.data]);
  const current = settings.data;
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  function submit(extra: { clear_key?: boolean } = {}) {
    save.mutate(
      { ...form, api_key: key || null, ...extra },
      {
        onSuccess: () => {
          setKey('');
          notifications.show({
            color: 'teal', title: 'Saved',
            message: extra.clear_key ? 'The API key was removed.' : 'Email verification updated.',
          });
        },
        onError: (e) => notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
      },
    );
  }

  return (
    <Card withBorder radius="md" padding="lg" data-testid="email-verification-card">
      <Stack>
        <Group gap="xs">
          <Text fw={600}>Email verification (EmailListVerify)</Text>
          <Badge color={current?.configured ? 'teal' : 'chartAmber'} variant="light">
            {current?.configured ? 'On' : current?.has_key ? 'Off' : 'Not set up'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Every marketing email is checked first. Free checks run on every address: spelling, known
          typos such as gmial.com, throwaway inboxes, and whether the domain takes mail at all. With
          a key, addresses that pass are then checked by EmailListVerify, one credit each, and the
          answer is kept so an address is not paid for twice.
        </Text>
        <PasswordInput
          label="API key"
          description="Write-only. Make one at emaillistverify.com under API."
          placeholder={current?.has_key ? `Saved, ending ${current.key_last4 || '****'}. Type a new one to replace it.` : ''}
          value={key}
          autoComplete="off"
          onChange={(e) => { const v = e.currentTarget.value; setKey(v); test.reset(); }}
        />
        <Group gap="sm">
          <Button
            variant="default"
            size="xs"
            loading={test.isPending}
            disabled={!key && !current?.has_key}
            onClick={() => test.mutate(key)}
          >
            Test
          </Button>
          {test.data?.ok && test.data.credits && (
            <Text size="sm" data-testid="elv-credits">
              Key works. <b>{formatNumber(test.data.credits.total)}</b> credits left
              {test.data.credits.subscription > 0
                ? ` (${formatNumber(test.data.credits.on_demand)} on demand, ${formatNumber(test.data.credits.subscription)} subscription)`
                : ''}.
            </Text>
          )}
          {test.data && !test.data.ok && <Text size="sm" c="red">{test.data.error}</Text>}
          {test.error && <Text size="sm" c="red">{test.error.message}</Text>}
        </Group>
        <Switch
          label="Verify with EmailListVerify"
          description="Off: the free checks only."
          checked={form.enabled}
          onChange={(e) => { const on = e.currentTarget.checked; set('enabled', on); }}
        />
        <Stack gap={4}>
          <Text size="sm" fw={500}>When the answer is unclear</Text>
          <Text size="xs" c="dimmed">
            Some mail servers will not say whether a mailbox exists. These cost no credit and are asked
            once more a day later. This also applies while EmailListVerify cannot be reached.
          </Text>
          <SegmentedControl
            size="xs"
            value={form.inconclusive_policy}
            onChange={(v) => set('inconclusive_policy', v)}
            data={[
              { value: 'skip', label: "Don't send (safer)" },
              { value: 'send', label: 'Send anyway' },
            ]}
          />
        </Stack>
        <Switch
          label="Send to accept-all domains"
          description="These accept every address at first and bounce later. Off is safer for your bounce rate."
          checked={form.send_ok_for_all}
          onChange={(e) => { const on = e.currentTarget.checked; set('send_ok_for_all', on); }}
        />
        <Switch
          label={`Check new ${v.many('lead')} as they arrive`}
          description={`Opted-in ${v.many('lead')} have their address checked after they are stored, never holding up routing. Uses a credit each.`}
          checked={form.verify_on_arrival}
          onChange={(e) => { const on = e.currentTarget.checked; set('verify_on_arrival', on); }}
        />
        <NumberInput
          label="Reuse an answer for"
          suffix=" days"
          min={1}
          max={365}
          maw={220}
          value={form.cache_days}
          onChange={(v) => set('cache_days', Number(v) || 90)}
        />
        {!current?.has_key && form.enabled && !key && (
          <Alert color="chartAmber" variant="light">
            <Text size="sm">Add an API key, or only the free checks will run.</Text>
          </Alert>
        )}
        <Group justify="flex-end">
          {current?.has_key && (
            <Button variant="subtle" color="red" disabled={save.isPending} onClick={() => submit({ clear_key: true })}>
              Remove key
            </Button>
          )}
          <Button loading={save.isPending} onClick={() => submit()}>Save</Button>
        </Group>
      </Stack>
    </Card>
  );
}
